import {
  ChangeDetectorRef,
  Component,
  EmbeddedViewRef,
  EventEmitter,
  Input,
  NgZone,
  OnDestroy,
  Output,
  TemplateRef,
  ViewContainerRef,
  inject,
} from '@angular/core';
import { CustomFormControlComponent } from '@shared/components/editor/custom-form-control/custom-form-control.component';
import { createPopper, Instance } from '@popperjs/core';
import { fromEvent } from 'rxjs';
import { filter, takeUntil } from 'rxjs/operators';

@Component({
  template: '',
  standalone: false,
})
export class PopupComponent extends CustomFormControlComponent implements OnDestroy {
  private vcr = inject(ViewContainerRef);
  private zone = inject(NgZone);
  protected cdr = inject(ChangeDetectorRef);

  @Output() opened = new EventEmitter();

  @Output() closed = new EventEmitter();

  @Input() optionWidth: number | undefined;

  protected view?: EmbeddedViewRef<any>;

  protected yOffset = 0;

  protected popperRef?: Instance;

  get isOpen(): boolean {
    return !!this.popperRef;
  }

  open(dropdownTpl: TemplateRef<any>, origin: HTMLElement): void {
    if (this.popperRef) {
      return;
    }
    this.view = this.vcr.createEmbeddedView(dropdownTpl);
    const popup = this.view?.rootNodes[0];

    document.body.appendChild(popup);
    popup.style.width = `${this.optionWidth ?? origin.offsetWidth}px`;

    this.zone.runOutsideAngular(() => {
      this.popperRef = createPopper(origin, popup, {
        placement: 'bottom-start',
        // 'fixed', not the default 'absolute': the popup is appended to document.body, and
        // while a MatDialog is open Material's BlockScrollStrategy makes <html>
        // `position: fixed; top: -<scrollY>px` (.cdk-global-scrollblock). Under the absolute
        // strategy Popper resolves offsets against that shifted ancestor and the popup lands
        // a whole scroll-height away from its trigger — off the top of the screen.
        strategy: 'fixed',
        modifiers: [
          {
            name: 'offset',
            options: {
              offset: [0, this.yOffset],
            },
          },
        ],
      });
      setTimeout(() => {
        const o = document.getElementById('search');
        if (o) {
          popup.style.width = `${this.optionWidth ?? o?.offsetWidth}px`;
        }
      }, 0);
    });
    this.handleClickOutside();
    this.opened.emit();
  }

  close(): void {
    this.closed?.emit();
    this.popperRef?.destroy();
    this.view?.destroy();
    this.view = undefined;
    this.popperRef = undefined;
  }

  ngOnDestroy(): void {
    this.close();
  }

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  writeValue(obj: any): void {}

  private handleClickOutside(): void {
    fromEvent(document, 'click')
      .pipe(
        filter(({ target }) => {
          const origin = this.popperRef?.state.elements.reference as HTMLElement;
          return !origin?.contains(target as HTMLElement);
        }),
        takeUntil(this.closed),
      )
      .subscribe((event) => {
        const popup = this.popperRef?.state.elements.popper;
        if (
          !!popup?.contains(event.target as HTMLElement) ||
          (event.target as HTMLElement).className.includes('mat-calendar')
        ) {
          return;
        }
        this.close();
        this.cdr.detectChanges();
      });
  }
}
